import React from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import ArticleLayout from "../Articles/layout/ArticleLayout";
import { useStore, KEYS, sortPosts } from "../admin/store";
import { isLoggedIn } from "../admin/auth";

export default function BlogArticle() {
    const { slug } = useParams();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const posts = useStore(KEYS.posts);
    const authors = useStore(KEYS.authors);

    const post = posts.find((p) => p.slug === slug);
    const isPreview = params.get("preview") === "1" && isLoggedIn();

    if (!post || (post.status !== "published" && !isPreview)) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-24 text-center">
                <Helmet>
                    <title>Article not found | MM AutoTech</title>
                    <meta name="robots" content="noindex" />
                </Helmet>
                <h1 className="text-3xl font-bold text-blue-900 mb-4">Article not found</h1>
                <p className="text-gray-600 mb-8">This article may have been moved or removed.</p>
                <Link to="/blog" className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700">
                    Back to Blog
                </Link>
            </div>
        );
    }

    const author = authors.find((a) => a.id === post.authorId);
    const published = posts.filter((p) => p.status === "published" && p.id !== post.id);
    let related = (post.related || [])
        .map((s) => published.find((p) => p.slug === s))
        .filter(Boolean);
    if (related.length === 0) {
        related = sortPosts(published.filter((p) => p.category === post.category)).slice(0, 2);
    }
    if (related.length === 0) related = sortPosts(published).slice(0, 2);

    const description = post.metaDescription || post.excerpt;

    return (
        <>
            <Helmet>
                <title>{`${post.title} | MM AutoTech`}</title>
                <meta name="description" content={description} />
                {author && <meta name="author" content={author.name} />}
                <link rel="canonical" href={`https://mmautotech.com/blog/${post.slug}`} />
                <meta property="og:title" content={post.title} />
                <meta property="og:description" content={description} />
                <meta property="og:type" content="article" />
                {post.image && !post.image.startsWith("data:") && (
                    <meta property="og:image" content={new URL(post.image, "https://mmautotech.com").href} />
                )}
                {isPreview && <meta name="robots" content="noindex" />}
            </Helmet>
            <ArticleLayout
                title={post.title}
                date={post.date}
                category={post.category}
                readTime={post.readTime}
                image={post.image}
                html={post.content}
                author={author}
                relatedArticles={related}
                onBack={() => navigate("/blog")}
                onArticleClick={(s) => navigate(`/blog/${s}`)}
                notice={
                    post.status !== "published" ? (
                        <div className="bg-amber-100 text-amber-900 text-sm text-center py-2 px-4">
                            Preview — this article is a <strong>draft</strong> and is not visible to visitors.
                        </div>
                    ) : null
                }
            />
        </>
    );
}
